// Your trips: what Latlens Maps kept while trip history was on. Places ranked
// by importance (visited often and lately first), total distance covered,
// distance per month and recent trips. The person's own data only.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { tripsApi, getErrorMessage, type TripSummary } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { StatTile } from '@/components/ui/StatTile';
import { ChartTooltip } from '@/components/charts/ChartTooltip';
import { CHART } from '@/lib/chartColors';
import { formatNumber } from '@/lib/utils';

const MAPS_URL = 'https://maps.latlens.com';
const MODE_LABEL: Record<string, string> = { drive: 'Drive', walk: 'Walk', bike: 'Bike', motorbike: 'Motorbike', keke: 'Keke', train: 'Train', brt: 'Bus', ferry: 'Ferry' };

export function km(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toLocaleString('en-NG', { maximumFractionDigits: m < 100_000 ? 1 : 0 })} km`;
}

function hours(s: number): string {
  const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
  return h ? `${h} h ${m} min` : `${m} min`;
}

const day = (t: number) => new Date(t * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const monthLabel = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });

function Off({ data }: { data: TripSummary }) {
  return (
    <Panel>
      <div className="mx-auto max-w-[52ch] py-10 text-center">
        <h2 className="text-lg font-semibold text-foreground">
          {data.settings.available ? 'Trip history is off' : 'Trip history isn’t offered in your country yet'}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {data.settings.available
            ? 'Switch on "Keep my trip history" in Latlens Maps settings. Each trip you finish then adds where you went, how far and how long, and this page ranks the places you go and totals your distance. Where you start and the path you take are never kept.'
            : 'Latlens offers trip history only where the country’s data protection requirements are in place. This page fills in once it is available and you switch it on in Latlens Maps.'}
        </p>
        <a href={MAPS_URL} target="_blank" rel="noopener" className="mt-5 inline-flex h-9 items-center rounded-full bg-accent px-4 text-[13px] font-medium text-[#0B0C0E] transition-transform hover:brightness-105 active:scale-[0.98]">
          Open Latlens Maps
        </a>
      </div>
    </Panel>
  );
}

export default function TripsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['trips-summary'], queryFn: () => tripsApi.summary().then(r => r.data) });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useMutation({
    mutationFn: () => tripsApi.deleteAll(),
    onSuccess: () => { setConfirmDelete(false); void qc.invalidateQueries({ queryKey: ['trips-summary'] }); },
  });

  const t = data?.totals;
  const empty = !!data && (!data.settings.enabled && data.totals.trips === 0);
  const maxScore = Math.max(1, ...(data?.places.map(p => p.score) ?? [1]));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Your trips"
        description={data?.settings.enabled
          ? `From Latlens Maps. Trips are kept for ${data.settings.retention_months} months; change that or switch it off in Maps settings.`
          : 'From Latlens Maps, when you switch on trip history.'}
        actions={data && data.totals.trips > 0 && (confirmDelete ? (
          <>
            <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending}
              className="inline-flex h-9 items-center rounded-full bg-error px-4 text-[13px] font-medium text-white disabled:opacity-60">Delete all trips</button>
            <button type="button" onClick={() => setConfirmDelete(false)} className="inline-flex h-9 items-center rounded-full border border-border px-4 text-[13px] font-medium text-foreground hover:bg-surface-raised">Cancel</button>
          </>
        ) : (
          <button type="button" onClick={() => setConfirmDelete(true)} className="inline-flex h-9 items-center rounded-full border border-border px-4 text-[13px] font-medium text-foreground hover:bg-surface-raised">Delete trip history</button>
        ))}
      />

      {!!error && <p role="alert" className="rounded-xl border border-error/30 bg-error/10 p-3 text-sm text-error">{getErrorMessage(error)}</p>}
      {!!remove.error && <p role="alert" className="rounded-xl border border-error/30 bg-error/10 p-3 text-sm text-error">{getErrorMessage(remove.error)}</p>}

      {empty ? <Off data={data!} /> : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile label="Distance covered" loading={isLoading} value={t ? km(t.distance_m) : ''}
              hint={t?.since ? `Since ${day(t.since)}` : undefined} trend={data?.by_month.map(m => m.distance_m)} />
            <StatTile label="Trips" loading={isLoading} value={t ? formatNumber(t.trips) : ''} hint={t ? `${formatNumber(t.arrivals)} reached the destination` : undefined} />
            <StatTile label="Places visited" loading={isLoading} value={t ? formatNumber(t.places) : ''} />
            <StatTile label="Time travelling" loading={isLoading} value={t ? hours(t.duration_s) : ''} />
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
            <Panel title="Places by importance" className="xl:col-span-3"
              action={<span className="text-xs text-muted-foreground">Visited often and lately ranks first</span>}>
              {isLoading ? (
                <div className="space-y-3" aria-hidden="true">{[0, 1, 2, 3].map(i => <div key={i} className="skeleton h-10 w-full" />)}</div>
              ) : !data?.places.length ? (
                <p className="py-6 text-center text-sm text-muted-foreground">No places yet. A place counts once you arrive there with directions on.</p>
              ) : (
                <ol className="divide-y divide-border">
                  {data.places.map((p, i) => (
                    <li key={`${p.lat},${p.lon}`} className="flex items-center gap-4 py-3">
                      <span className="w-6 shrink-0 text-right font-mono text-sm tabular-nums text-muted-foreground">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{p.name}</p>
                        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-surface-raised" aria-hidden="true">
                          <div className="h-full rounded-full bg-accent" style={{ width: `${Math.max(4, (p.score / maxScore) * 100)}%` }} />
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="font-mono text-sm tabular-nums text-foreground">{p.visits} {p.visits === 1 ? 'visit' : 'visits'}</p>
                        <p className="text-xs text-muted-foreground">Last {day(p.last_visit)}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </Panel>

            <Panel title="Distance by month" className="xl:col-span-2" action={<span className="text-xs text-muted-foreground">km</span>}>
              {isLoading ? <div className="skeleton h-56 w-full" aria-hidden="true" /> : (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data?.by_month ?? []} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" />
                      <XAxis dataKey="month" tickFormatter={monthLabel} tickLine={false} axisLine={false} minTickGap={8} />
                      <YAxis tickFormatter={(v: number) => `${Math.round(v / 1000)}`} tickLine={false} axisLine={false} width={36} allowDecimals={false} />
                      <Tooltip content={<ChartTooltip format={km} />} labelFormatter={monthLabel} cursor={{ fill: 'rgb(var(--color-border) / 0.4)' }} />
                      <Bar dataKey="distance_m" name="Distance" fill={CHART.primary} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Panel>
          </div>

          <Panel title="Recent trips" bodyClassName="p-0 pt-0">
            {isLoading ? <div className="skeleton m-5 h-24" aria-hidden="true" /> : !data?.recent.length ? (
              <p className="px-5 pb-6 pt-2 text-sm text-muted-foreground">No trips yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs text-muted-foreground">
                      <th className="px-5 py-2.5 font-medium">To</th>
                      <th className="px-5 py-2.5 font-medium">How</th>
                      <th className="px-5 py-2.5 text-right font-medium">Distance</th>
                      <th className="px-5 py-2.5 text-right font-medium">Time</th>
                      <th className="px-5 py-2.5 text-right font-medium">Ended</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.recent.map(r => (
                      <tr key={`${r.ended_at}-${r.name}`}>
                        <td className="px-5 py-3 text-foreground">{r.name}{!r.arrived && <span className="ml-2 text-xs text-muted-foreground">(ended early)</span>}</td>
                        <td className="px-5 py-3 text-muted-foreground">{MODE_LABEL[r.mode] ?? r.mode}</td>
                        <td className="px-5 py-3 text-right font-mono tabular-nums">{km(r.distance_m)}</td>
                        <td className="px-5 py-3 text-right font-mono tabular-nums">{hours(r.duration_s)}</td>
                        <td className="px-5 py-3 text-right text-muted-foreground">{day(r.ended_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
